import type {
    RecommendedRingSizeFragment,
    RingSizeFragment,
    RingSizeInput,
} from '@/graphql/api'
import { assertNever } from '@/helpers'
import { computed, ref, type Ref, watch } from 'vue'
import type { SelectOption } from '@/components/ui/inputs/th-select.ts'

const prefixes = ['RecommendedRingSize', 'RingSize'] as const
type RingSizePrefix = (typeof prefixes)[number]
export type RingRecommendedSizeOrSizeValue = `${RingSizePrefix}|${string}`

function isPrefix(t: string | undefined): t is RingSizePrefix {
    return prefixes.includes(t as RingSizePrefix)
}

function toInput(value: RingRecommendedSizeOrSizeValue): RingSizeInput {
    const [type, id] = value.split('|')

    if (!isPrefix(type)) {
        return {}
    }

    switch (type) {
        case 'RecommendedRingSize':
            return {
                recommendedRingSizeId: id,
            }
        case 'RingSize':
            return {
                ringSizeId: id,
            }
        default:
            return assertNever(type, {})
    }
}

export function useRingSize(
    sizes: Readonly<
        Ref<
            | Array<RecommendedRingSizeFragment | RingSizeFragment>
            | undefined
            | null
        >
    >,
    persisted?: Ref<RingRecommendedSizeOrSizeValue | null>
) {
    const ringSizeId = ref<RingRecommendedSizeOrSizeValue | null>(
        persisted?.value ??
            (sizes.value?.[0]
                ? `${sizes.value[0].__typename}|${sizes.value[0].id}`
                : null)
    )

    const ringSizeOptions = computed<
        SelectOption<RingRecommendedSizeOrSizeValue>[]
    >(
        () =>
            sizes.value?.map((size) => ({
                value: `${size.__typename}|${size.id}`,
                label:
                    size.__typename === 'RingSize'
                        ? size.ringSizeLabel || ''
                        : size.label,
            })) || []
    )

    watch(ringSizeOptions, (newValue) => {
        if (
            !newValue.length ||
            !newValue.find((option) => option.value === ringSizeId.value)
        ) {
            ringSizeId.value = null
        }
    })

    const ringSizeError = ref('')

    const clearRingSizeNotSelectedError = () => {
        ringSizeError.value = ''
    }

    const handleRingSizeNotSelected = () => {
        const id = ringSizeId.value

        if (!id) {
            // TODO Translate
            ringSizeError.value = 'Please select a ring size'
        } else {
            clearRingSizeNotSelectedError()
        }

        return id && toInput(id)
    }

    watch(ringSizeId, (size) => {
        if (size) {
            clearRingSizeNotSelectedError()
        }

        if (persisted) {
            persisted.value = size
        }
    })

    return {
        ringSizeId,
        ringSizeOptions,
        ringSizeError,
        handleRingSizeNotSelected,
    }
}
